import { useState } from 'react'
import { PageLayout } from '@/shared/components/PageLayout'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import Lightbox from 'yet-another-react-lightbox'
import 'yet-another-react-lightbox/styles.css'
import { content } from '@/lib/content'
import { Link } from 'react-router-dom'
import { PAGE_URLS } from '@/lib/constants'
import {
  Camera,
  ZoomIn,
  Phone,
  ArrowRight
} from 'lucide-react'

const galleryImages = content.gallery.images

// Create a pattern for masonry-like layout
const getImageSize = (index) => {
  const pattern = [
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-1 row-span-2',
    'col-span-1 row-span-1',
    'col-span-2 row-span-1',
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-1 row-span-2',
    'col-span-1 row-span-1',
    'col-span-1 row-span-1',
    'col-span-2 row-span-1',
    'col-span-1 row-span-1',
  ]
  return pattern[index % pattern.length]
}

export default function GalleryPage() {
  const [lightboxOpen, setLightboxOpen] = useState(false)
  const [photoIndex, setPhotoIndex] = useState(0)

  const openLightbox = (index) => {
    setPhotoIndex(index)
    setLightboxOpen(true)
  }

  return (
    <PageLayout title={content.gallery.seo.title} description={content.gallery.seo.description}>
      {/* Hero Header */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 pattern-bg" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/50 to-background" />

        <div className="relative content-container text-center">
          <Badge variant="outline" className="mb-6 border-primary/50 bg-primary/10 text-primary px-4 py-1.5">
            <Camera className="mr-2 h-3.5 w-3.5" />
            Photo Gallery
          </Badge>

          <h1 className="text-4xl md:text-6xl font-bold text-foreground mb-6">
            {content.gallery.heading}
          </h1>

          <Separator className="w-16 bg-primary mx-auto mb-6" />

          <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
            {content.gallery.description}
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="section-padding pt-8">
        <div className="content-container">
          {/* Masonry Grid */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 auto-rows-[200px] gap-4">
            {galleryImages.map((image, index) => (
              <div
                key={index}
                className={`group relative overflow-hidden rounded-lg bg-card border border-border cursor-pointer transition-all duration-300 hover:border-primary/50 hover:shadow-xl hover:shadow-primary/10 ${getImageSize(index)}`}
                onClick={() => openLightbox(index)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && openLightbox(index)}
                aria-label={`View ${image.alt} in lightbox`}
              >
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />

                {/* Hover Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-sm font-medium text-foreground">
                        {image.alt}
                      </span>
                      <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center">
                        <ZoomIn className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Corner Accent */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-primary/20 transform rotate-45 translate-x-12 -translate-y-12" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-secondary/30">
        <div className="content-container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Ready to Create Your Own Memories?
            </h2>
            <p className="text-muted-foreground mb-8">
              Book KC Hall for your next event and let us help make it unforgettable.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to={PAGE_URLS.CONTACT}>
                <Button size="lg" className="cursor-pointer">
                  <Phone className="mr-2 h-5 w-5" />
                  Contact Us
                </Button>
              </Link>
              <Link to={PAGE_URLS.SERVICES}>
                <Button size="lg" variant="outline" className="cursor-pointer">
                  View Our Services
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      <Lightbox
        open={lightboxOpen}
        close={() => setLightboxOpen(false)}
        index={photoIndex}
        slides={galleryImages}
        styles={{
          container: { backgroundColor: 'rgba(26, 22, 20, 0.95)' },
        }}
      />
    </PageLayout>
  )
}
